/**
 * Auth view-model + route-guard logic (PURE). Roles come from Firebase custom
 * claims (`role: 'creator' | 'superadmin'`), read off the decoded ID token. This
 * module models the signed-in user and the guard decision so route protection
 * is unit-tested without React Router (the spec lists "route guards by role").
 */
import type { Role, AuthClaims } from '@djgg/creations';
import { hasRole, isPlatformOperator } from '@djgg/creations';

/** The signed-in user as the portal sees it (null = signed out). */
export interface PortalUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  /** Custom-claim role, or undefined for a plain authenticated user. */
  role?: Role;
  /** True for an anonymous/guest sign-in (can browse but not like/create). */
  isAnonymous: boolean;
}

/** Auth state the app tracks. */
export interface AuthState {
  /** Still resolving the initial auth check. */
  loading: boolean;
  user: PortalUser | null;
}

/** The initial (loading) auth state. */
export const INITIAL_AUTH: AuthState = { loading: true, user: null };

/** Project a portal user into the claims shape the @djgg/creations helpers read. */
export function toClaims(user: PortalUser | null): AuthClaims | null {
  if (!user) return null;
  return { uid: user.uid, role: user.role };
}

/** What a route requires to render. */
export type RouteRequirement =
  /** Any visitor (even signed out). */
  | 'public'
  /** A signed-in, non-anonymous user. */
  | 'authed'
  /** Role `creator` (superadmin implies it). */
  | 'creator'
  /** Role `superadmin`. */
  | 'superadmin'
  /** Superadmin OR a uid in the PLATFORM_OPERATORS allowlist. */
  | 'adminStats';

/** The guard's verdict for a route. */
export type GuardVerdict =
  | { allow: true }
  | { allow: false; redirectTo: string; reason: string };

/**
 * Decide whether `user` may enter a route with the given `requirement`. Returns
 * a redirect target + reason when denied. Signed-out users are sent to /login;
 * an under-privileged signed-in user is sent to / (they exist but lack the
 * role).
 */
export function guard(user: PortalUser | null, requirement: RouteRequirement): GuardVerdict {
  if (requirement === 'public') return { allow: true };

  if (!user || user.isAnonymous) {
    return { allow: false, redirectTo: '/login', reason: 'not signed in' };
  }

  if (requirement === 'authed') return { allow: true };

  const claims = toClaims(user);

  // Admin Stats: the allowlist gate (superadmin OR an allowlisted uid). This is
  // COSMETIC — the getAdminStats callable re-checks the same allowlist server-
  // side, and the snapshot table is RLS deny-all. It only hides the nav/route.
  if (requirement === 'adminStats') {
    if (isPlatformOperator(claims)) return { allow: true };
    return { allow: false, redirectTo: '/', reason: 'not an admin-stats viewer' };
  }

  const role: Role = requirement;
  if (hasRole(claims, role)) return { allow: true };

  return { allow: false, redirectTo: '/', reason: `requires role ${role}` };
}

/** Convenience booleans for the header/nav (which links to show). */
export function isCreator(user: PortalUser | null): boolean {
  return hasRole(toClaims(user), 'creator');
}
/** Any real signed-in account may create and fork games. */
export function canCreate(user: PortalUser | null): boolean {
  return Boolean(user && !user.isAnonymous);
}
export function isSuperadmin(user: PortalUser | null): boolean {
  return hasRole(toClaims(user), 'superadmin');
}
export function isStatsViewer(user: PortalUser | null): boolean {
  return isPlatformOperator(toClaims(user));
}
